import { Injectable, Optional } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, concatMap, map, mergeMap, Observable, of } from 'rxjs';
import { HWServiceConfig } from './hw-service-config';
import { Convert } from 'types';

export enum ComputerState {
  Unknown,
  Off,
  On,
}

export enum ServiceState {
  Unknown,
  Off,
  On,
}

export enum LockState {
  Unknown,
  Unlocked,
  Locked,
}

export class PhotoCabinHostState {
  ComputerState: ComputerState;
  ServiceState: ServiceState;
}

export class PhotoCabinState {
  HostState: PhotoCabinHostState;
  LockState: LockState;
}

export class PhotoCabinInfo {
  Id: number;
  Number: number;
  Name: string;
  Address: string;
  MacAddress: number[] | null;
  Type: string;
  State: PhotoCabinState;
}

export abstract class PhotoCabinService {
  //  * Получить список кабин для конкретной клиентской машины (веб-конфигуратор)
  abstract getPhotoCabinsForMachine(machine: string): Observable<PhotoCabinInfo[]>;
  // * Получить список кабин для текущей клиентской машины
  abstract getPhotoCabinsForClientMachine(): Observable<PhotoCabinInfo[]>;
  // * Получить список всех кабин (веб-конфигуратор)
  abstract getPhotoCabins(): Observable<PhotoCabinInfo[]>;
  // * Получить статус кабины
  abstract getState(id: number): Observable<PhotoCabinState>;
  // * Инициировать включение кабины
  abstract initiateTurnOn(id: number): Observable<boolean>;
  // * Инициировать выключение кабины
  abstract initiateTurnOff(id: number): Observable<boolean>;
  // * Добавить кабину (веб-конфигуратор)
  abstract addPhotoCabin(info: PhotoCabinInfo): Observable<boolean>;
  // * Удалить кабину (веб-конфигуратор)
  abstract removePhotoCabin(id: number): Observable<boolean>;
  // * Редактирование информации о кабине (веб-конфигуратор)
  abstract changePhotoCabin(info: PhotoCabinInfo): Observable<boolean>;
  // * Занять фотокабину
  abstract lockPhotoCabin(id: number): Observable<boolean>;
  // * Удерживать фотокабину (вызывать по таймеру)
  abstract touchPhotoCabinLock(id: number): Observable<boolean>;
  // * Разблокировать фотокабину
  abstract unlockPhotoCabin(id: number): Observable<boolean>;
}

export enum CameraSelectionEnumForSet {
  Top = 0,
  Bottom = 1,
  Auto = 2,
}

export enum CameraSelectionEnumForGet {
  Top = 0,
  Bottom = 1,
}

export class CameraSelectionDataForGet {
  public current: CameraSelectionEnumForGet;
  public isAutoMode: boolean;
}

export class AdjustStateItem {
  cameraId: CameraSelectionEnumForGet;
  parameter: 'exposure' | 'focus';
  stage: 'tuning' | 'done' | 'reached-limit';
}

export class EyePosition {
  X: number;
  Y: number;

  constructor(X: number, Y: number) {
    this.X = X;
    this.Y = Y;
  }
}

export class Eyes {
  Left: EyePosition;
  Right: EyePosition;
  IsFound: boolean;

  constructor(Left: EyePosition, Right: EyePosition, IsFound: boolean) {
    this.Left = Left;
    this.Right = Right;
    this.IsFound = IsFound;
  }
}

export class QualityFlags {
  Glares: boolean;
  BadWhiteBalance: boolean;
  BadFocus: boolean;
  BadContrast: boolean;
  BadPose: boolean;
  BadAngle: boolean;
  BadEncoding: boolean;
  IrregularIllumination: boolean;
}

export class BinaryShootingResult {
  Photo: string;
  LeftEye: EyePosition;
  RightEye: EyePosition;
  ChipPhoto: string;
  PrintPhoto: string;
  Quality: QualityFlags;

  constructor(Photo: string, LeftEye: EyePosition, RightEye: EyePosition) {
    this.Photo = Photo;
    this.LeftEye = LeftEye;
    this.RightEye = RightEye;
  }
}

export abstract class PhotoCabinControllerService1 {
  // * Url на видео-поток
  abstract startStreamingURL(id: number): Observable<string>;
  // * Двигать камеру
  abstract move(id: number, direction: MovementDirection): Observable<void>;
  // * Сфоткать
  abstract shoot(id: number): Observable<BinaryShootingResult>;
  // * Обрезать (вызывать руками)
  abstract crop(id: number, left_x: number, left_y: number, right_x: number, right_y: number): Observable<BinaryShootingResult>;
  // * Отмена соединения
  abstract cancel(id: number): Observable<void>;
}

export abstract class PhotoCabinControllerService2 {
  // * Открыть соединение с камерой
  abstract open(id: number): Observable<void>;
  // * Закрыть соединение с камерой
  abstract close(id: number): Observable<void>;
  // * Url на видео-поток
  abstract startStreamingURL(id: number): Observable<string>;
  // * Выбрать камеру
  abstract selectCamera(id: number, selection: CameraSelectionEnumForSet): Observable<void>;
  // * Получить выбранную камеру
  abstract getSelectedCamera(id: number): Observable<CameraSelectionDataForGet>;
  // * Получить текущее состояние автонастройки
  abstract getAdjustState(id: number): Observable<AdjustStateItem[]>;
  // * Сделать кадр
  abstract shoot(id: number, selection: CameraSelectionEnumForGet): Observable<void>;
  // * Найти глаза
  abstract findEyes(id: number): Observable<Eyes>;
  // * Обрезать кадр
  abstract crop(id: number, left_x: number, left_y: number, right_x: number, right_y: number): Observable<BinaryShootingResult>;
  // * Скачать фото с сервера
  abstract getSnapshot(id: number): Observable<string>;
  // * Завершить съемку
  abstract cancel(id: number): Observable<void>;
  // * Вкл/выкл подсветку
  abstract setLight(id: number, on: boolean): Observable<void>;
}

@Injectable()
export class HttpPhotoCabinService implements PhotoCabinService {
  constructor(private http: HttpClient, private config: HWServiceConfig) {}

  private httpPost<T>(method: string, body: any): Observable<T> {
    return this.http
      .post<{ d: T }>(this.config.getPhotoServiceAddress() + method, body, { headers: { 'Content-Type': 'application/json' } })
      .pipe(map((x) => x.d));
  }

  getPhotoCabinsForMachine(machine: string): Observable<PhotoCabinInfo[]> {
    return this.httpPost('GetPhotoCabinsForMachine', { machine });
  }

  getPhotoCabinsForClientMachine(): Observable<PhotoCabinInfo[]> {
    return new Observable((observer) => {
      const eventSource = new EventSource(this.config.getPhotoServiceAddress() + 'GetPhotoCabinsForClientMachine');
      eventSource.onmessage = (message) => observer.next(message.data);
      eventSource.onerror = (error) => observer.error(error);
      return () => {
        eventSource.close();
      };
    }).pipe(map((serialized: any) => JSON.parse(serialized)));
  }

  getPhotoCabins(): Observable<PhotoCabinInfo[]> {
    return this.httpPost('GetPhotoCabins', {});
  }

  getState(id: number): Observable<PhotoCabinState> {
    return this.httpPost('GetState', { id });
  }

  initiateTurnOn(id: number): Observable<boolean> {
    return this.httpPost('InitiateTurnOn', { id });
  }

  initiateTurnOff(id: number): Observable<boolean> {
    return this.httpPost('InitiateTurnOff', { id });
  }

  addPhotoCabin(info: PhotoCabinInfo): Observable<boolean> {
    return this.httpPost('AddPhotoCabin', { info });
  }

  removePhotoCabin(id: number): Observable<boolean> {
    return this.httpPost('RemovePhotoCabin', { id });
  }

  changePhotoCabin(info: PhotoCabinInfo): Observable<boolean> {
    return this.httpPost('ChangePhotoCabin', { info });
  }

  lockPhotoCabin(id: number): Observable<boolean> {
    return this.httpPost('LockPhotoCabin', { id });
  }

  touchPhotoCabinLock(id: number): Observable<boolean> {
    return new Observable((observer) => {
      const eventSource = new EventSource(this.config.getPhotoServiceAddress() + 'TouchPhotoCabinLockPush/' + id);
      eventSource.onmessage = (message) => observer.next(message.data);
      eventSource.onerror = (error) => observer.error(error);
      return () => {
        eventSource.close();
      };
    }).pipe(map((serialized: any) => JSON.parse(serialized).result));
  }

  unlockPhotoCabin(id: number): Observable<boolean> {
    return this.httpPost('UnlockPhotoCabin', { id });
  }
}

@Injectable()
export class HttpPhotoCabinControllerService1 implements PhotoCabinControllerService1 {
  constructor(private http: HttpClient, private config: HWServiceConfig) {}

  private httpPost<T>(method: string, body: any): Observable<T> {
    return this.http
      .post<{ d: T }>(this.config.getPhotoControllerV1Address() + method, body, { headers: { 'Content-Type': 'application/json' } })
      .pipe(map((x) => x.d));
  }

  startStreamingURL(id: number): Observable<string> {
    return this.httpPost<string>('StartStreamingURL', { id }).pipe(map((x) => this.config.getPhotoControllerV1PreviewAddress(x)));
  }

  move(id: number, direction: MovementDirection): Observable<void> {
    return this.httpPost('Move', { id, direction });
  }

  shoot(id: number): Observable<BinaryShootingResult> {
    return this.httpPost('ShootOptimizedForWeb', { id });
  }

  crop(id: number, left_x: number, left_y: number, right_x: number, right_y: number): Observable<BinaryShootingResult> {
    return this.httpPost('CropOptimizedForWeb', { id, left_x, left_y, right_x, right_y });
  }

  cancel(id: number): Observable<void> {
    return this.httpPost('Cancel', { id });
  }
}

@Injectable()
export class HttpPhotoCabinControllerService2 implements PhotoCabinControllerService2 {
  constructor(private http: HttpClient, private config: HWServiceConfig) {}

  private httpPost<T>(method: string, body: any): Observable<T> {
    return this.http
      .post<{ d: T }>(this.config.getPhotoControllerV2Address() + method, body, { headers: { 'Content-Type': 'application/json' } })
      .pipe(map((x) => x.d));
  }

  private httpPostBinary(method: string, body: any): Observable<Blob> {
    return this.http.post(this.config.getPhotoControllerV2Address() + method, body, {
      headers: { 'Content-Type': 'application/json' },
      responseType: 'blob',
    });
  }

  open(id: number): Observable<void> {
    return this.httpPost('Open', { id });
  }

  close(id: number): Observable<void> {
    return this.httpPost('Close', { id });
  }

  startStreamingURL(id: number): Observable<string> {
    return this.httpPost('StartStreamingURL', { id });
  }

  selectCamera(id: number, selection: CameraSelectionEnumForSet): Observable<void> {
    return this.httpPost('SelectCamera', { id, selection });
  }

  getSelectedCamera(id: number): Observable<CameraSelectionDataForGet> {
    return this.httpPost('GetSelectedCamera', { id });
  }

  getAdjustState(id: number): Observable<AdjustStateItem[]> {
    return this.httpPost('GetAdjustState', { id });
  }

  shoot(id: number, selection: CameraSelectionEnumForGet): Observable<void> {
    return this.httpPost('Shoot', { id, selection });
  }

  findEyes(id: number): Observable<Eyes> {
    return this.httpPost('FindEyes', { id });
  }

  crop(id: number, left_x: number, left_y: number, right_x: number, right_y: number): Observable<BinaryShootingResult> {
    return this.httpPost('CropOptimizedForWeb', { id, left_x, left_y, right_x, right_y });
  }

  getSnapshot(id: number): Observable<string> {
    return this.httpPostBinary('GetSnapshotOptimizedForWeb', { id }).pipe(concatMap((x) => Convert.toBase64$(x)));
  }

  cancel(id: number): Observable<void> {
    return this.httpPost('Cancel', { id });
  }

  setLight(id: number, on: boolean): Observable<void> {
    return this.httpPost('SetLight', { id, on });
  }
}

export enum PhotoCabinType {
  Inforser = 'Инфорсер',
  Spektr1 = 'Спектр v1',
  Spektr2 = 'Спектр v2',
}

export enum PhotoCabinCapabilities {
  Speakerphone,
  CameraSelection,
  AutoCameraSelection,
}

export enum MovementDirection {
  Up = 0,
  Down = 1,
  Stop = 2,
}

export class PhotoCabinControllerCommandContext {
  get type(): PhotoCabinType {
    if (this.controller1) {
      return PhotoCabinType.Inforser;
    } else if (this.controller2) {
      return PhotoCabinType.Spektr2;
    } else {
      throw new Error('Cannot get photo cabin type. No photo cabin controllers where injected.');
    }
  }

  get union(): PhotoCabinControllerService1 | PhotoCabinControllerService2 | null {
    return this.controller1 || this.controller2;
  }

  get capabilities(): PhotoCabinCapabilities[] {
    if (this.controller1) {
      return [];
    } else if (this.controller2) {
      return [PhotoCabinCapabilities.Speakerphone, PhotoCabinCapabilities.CameraSelection, PhotoCabinCapabilities.AutoCameraSelection];
    } else {
      return [];
    }
  }

  constructor(public controller1: PhotoCabinControllerService1 | null, public controller2: PhotoCabinControllerService2 | null) {}
}

export interface PhotoCabinControllerCommand {
  execute(ctx: PhotoCabinControllerCommandContext, ...args: any): Observable<any>;
}

export class OpenPhotoCabinCommand implements PhotoCabinControllerCommand {
  execute(ctx: PhotoCabinControllerCommandContext, id: number): Observable<void> {
    if (ctx.controller1) {
      return of(undefined);
    } else if (ctx.controller2) {
      return ctx.controller2.open(id);
    }

    throw new Error('Command not supported');
  }
}

export class ClosePhotoCabinCommand implements PhotoCabinControllerCommand {
  execute(ctx: PhotoCabinControllerCommandContext, id: number): Observable<void> {
    if (ctx.controller2) {
      return ctx.controller2.close(id);
    }

    throw new Error('Command not supported');
  }
}

export class StartStreamingURLPhotoCabinCommand implements PhotoCabinControllerCommand {
  execute(ctx: PhotoCabinControllerCommandContext, id: number): Observable<string> {
    return ctx.union!.startStreamingURL(id);
  }
}

export class MovePhotoCabinCommand implements PhotoCabinControllerCommand {
  execute(ctx: PhotoCabinControllerCommandContext, id: number, direction: MovementDirection): Observable<void> {
    if (ctx.controller1) {
      return ctx.controller1.move(id, direction);
    }

    throw new Error('Command not supported.');
  }
}

export class SelectCameraPhotoCabinCommand implements PhotoCabinControllerCommand {
  execute(ctx: PhotoCabinControllerCommandContext, id: number, selection: CameraSelectionEnumForSet): Observable<void> {
    if (ctx.controller2) {
      return ctx.controller2.selectCamera(id, selection);
    }

    throw new Error('Command not supported.');
  }
}

export class GetSelectedCameraPhotoCabinCommand implements PhotoCabinControllerCommand {
  execute(ctx: PhotoCabinControllerCommandContext, id: number): Observable<CameraSelectionDataForGet> {
    if (ctx.controller2) {
      return ctx.controller2.getSelectedCamera(id);
    }

    return of({ current: CameraSelectionEnumForGet.Top, isAutoMode: true });
  }
}

export class ShootPhotoCabinCommand implements PhotoCabinControllerCommand {
  execute(ctx: PhotoCabinControllerCommandContext, id: number, selection: CameraSelectionEnumForGet): Observable<BinaryShootingResult> {
    if (ctx.controller1) {
      return ctx.controller1.shoot(id);
    } else if (ctx.controller2) {
      return ctx.controller2.shoot(id, selection).pipe(
        mergeMap((_) => ctx.controller2!.getSnapshot(id)),
        mergeMap((photo) =>
          ctx.controller2!.findEyes(id).pipe(
            map((eyes) => new BinaryShootingResult(photo, eyes.Left, eyes.Right)),
            catchError((err) => {
              throw `Cannot find eyes: ${err}`;
            }),
          ),
        ),
      );
    }

    throw new Error('Command not supported.');
  }
}

export class FindEyesPhotoCabinCommand implements PhotoCabinControllerCommand {
  execute(ctx: PhotoCabinControllerCommandContext, id: number): Observable<Eyes> {
    if (ctx.controller2) {
      return ctx.controller2.findEyes(id);
    }

    throw new Error('Command not supported.');
  }
}

export class CropPhotoCabinCommand implements PhotoCabinControllerCommand {
  execute(
    ctx: PhotoCabinControllerCommandContext,
    id: number,
    left_x: number,
    left_y: number,
    right_x: number,
    right_y: number,
  ): Observable<BinaryShootingResult> {
    return ctx.union!.crop(id, Math.trunc(left_x), Math.trunc(left_y), Math.trunc(right_x), Math.trunc(right_y));
  }
}

export class GetSnapshotPhotoCabinCommand implements PhotoCabinControllerCommand {
  execute(ctx: PhotoCabinControllerCommandContext, id: number): Observable<string> {
    if (ctx.controller2) {
      return ctx.controller2.getSnapshot(id);
    }

    throw new Error('Command not supported.');
  }
}

export class CancelPhotoCabinCommand implements PhotoCabinControllerCommand {
  execute(ctx: PhotoCabinControllerCommandContext, id: number): Observable<void> {
    return ctx.union!.cancel(id);
  }
}

export class SetLightPhotoCabinCommand implements PhotoCabinControllerCommand {
  execute(ctx: PhotoCabinControllerCommandContext, id: number, on: boolean): Observable<void> {
    if (ctx.controller2) {
      return ctx.controller2.setLight(id, on);
    }

    throw new Error('Command not supported.');
  }
}

export class PhotoCabinControllerAdapter {
  /**
   * Возвращает тип кабины. Лучше использовать {@link PhotoCabinControllerAdapter.capabilities}
   */
  get type(): PhotoCabinType {
    return this.context.type;
  }

  /**
   * Возвращает дополнительные возможности кабины (например, наличие нескольких камер)
   */
  get capabilities(): PhotoCabinCapabilities[] {
    return this.context.capabilities;
  }

  private context: PhotoCabinControllerCommandContext;

  constructor(controller1: PhotoCabinControllerService1 | null, controller2: PhotoCabinControllerService2 | null) {
    this.context = new PhotoCabinControllerCommandContext(controller1, controller2);
  }

  execute<T extends PhotoCabinControllerCommand>(
    command: T,
    ...args: Parameters<T['execute']> extends [any, ...infer U] ? U : never
  ): ReturnType<T['execute']> {
    return command.execute(this.context, ...args) as any;
  }
}

@Injectable()
export class PhotoCabinControllerAdapterBuilder {
  constructor(
    @Optional() private controller1: PhotoCabinControllerService1 | null,
    @Optional() private controller2: PhotoCabinControllerService2 | null,
  ) {}

  create(type: string): PhotoCabinControllerAdapter {
    if ((type === PhotoCabinType.Inforser || type === PhotoCabinType.Spektr1) && this.controller1 !== null) {
      return new PhotoCabinControllerAdapter(this.controller1, null);
    } else if (type === PhotoCabinType.Spektr2 && this.controller2 !== null) {
      return new PhotoCabinControllerAdapter(null, this.controller2);
    } else {
      throw new Error(
        `Cannot create "PhotoCabinControllerAdapter". Have you provided correct "PhotoCabinControllerService" for cabin type "${type}"?`,
      );
    }
  }
}

export const PhotoCabinCommands = {
  Open: OpenPhotoCabinCommand,
  Close: ClosePhotoCabinCommand,
  StartStreamingURL: StartStreamingURLPhotoCabinCommand,
  Move: MovePhotoCabinCommand,
  SelectCamera: SelectCameraPhotoCabinCommand,
  GetSelectedCamera: GetSelectedCameraPhotoCabinCommand,
  Shoot: ShootPhotoCabinCommand,
  FindEyes: FindEyesPhotoCabinCommand,
  Crop: CropPhotoCabinCommand,
  GetSnapshot: GetSnapshotPhotoCabinCommand,
  Cancel: CancelPhotoCabinCommand,
  SetLight: SetLightPhotoCabinCommand,
};
